// ─── Types ────────────────────────────────────────────────────────────────────

export type Difficulty = 'Easy' | 'Medium' | 'Advanced';

export interface TutorialStep {
  text: string;
  imagePlaceholder?: string;
  imageUrl?: string;
}

export interface TutorialArticle {
  id: string;
  title: string;
  difficulty: Difficulty;
  estimatedTime: string;
  description: string;
  steps: TutorialStep[];
  hasVideoSlot?: boolean;
  videoUrl?: string;
  relatedArticleIds?: string[];
}

export interface TutorialCategory {
  id: string;
  title: string;
  description: string;
  intro?: string;
  articles: TutorialArticle[];
}

export interface TutorialProduct {
  id: string;
  title: string;
  subtitle: string;
  audience: string;
  description: string;
  categories: TutorialCategory[];
}

// ─── Raw content types (what the content file uses) ───────────────────────────

interface RawStep {
  text: string;
  imagePlaceholder?: string;
  imageUrl?: string;
}

interface RawArticle {
  id: string;
  title: string;
  difficulty: Difficulty;
  time: string;
  desc: string;
  steps: RawStep[];
  video?: boolean | string;
  related?: string[];
}

interface RawCategory {
  id: string;
  title: string;
  description: string;
  intro?: string;
  articles: RawArticle[];
}

interface RawProduct {
  id: string;
  title: string;
  subtitle: string;
  audience: string;
  description: string;
  categories: RawCategory[];
}

// ─── Normalize raw → typed ────────────────────────────────────────────────────

function normalizeProduct(raw: RawProduct): TutorialProduct {
  return {
    id: raw.id,
    title: raw.title,
    subtitle: raw.subtitle,
    audience: raw.audience,
    description: raw.description,
    categories: raw.categories.map((cat) => ({
      id: cat.id,
      title: cat.title,
      description: cat.description,
      intro: cat.intro,
      articles: cat.articles.map((art) => ({
        id: art.id,
        title: art.title,
        difficulty: art.difficulty,
        estimatedTime: art.time,
        description: art.desc,
        steps: art.steps,
        hasVideoSlot: !!art.video,
        videoUrl: typeof art.video === 'string' ? art.video : undefined,
        relatedArticleIds: art.related ?? [],
      })),
    })),
  };
}

// ─── Import and export typed products ────────────────────────────────────────

import { DASHBOARD, APP_MOBILE } from './tutorials-content';

export const TUTORIAL_PRODUCTS: TutorialProduct[] = [
  normalizeProduct(DASHBOARD as unknown as RawProduct),
  normalizeProduct(APP_MOBILE as unknown as RawProduct),
];

// ─── Lookup helpers ───────────────────────────────────────────────────────────

export function getProduct(productId: string): TutorialProduct | undefined {
  return TUTORIAL_PRODUCTS.find((p) => p.id === productId);
}

export function getCategory(productId: string, categoryId: string): TutorialCategory | undefined {
  return getProduct(productId)?.categories.find((c) => c.id === categoryId);
}

export function getArticle(
  productId: string,
  categoryId: string,
  articleId: string,
): TutorialArticle | undefined {
  return getCategory(productId, categoryId)?.articles.find((a) => a.id === articleId);
}

export function findArticleById(
  product: TutorialProduct,
  articleId: string,
): { category: TutorialCategory; article: TutorialArticle } | undefined {
  for (const category of product.categories) {
    const article = category.articles.find((a) => a.id === articleId);
    if (article) return { category, article };
  }
  return undefined;
}

export function getAdjacentArticles(
  product: TutorialProduct,
  categoryId: string,
  articleId: string,
) {
  const all: { category: TutorialCategory; article: TutorialArticle }[] = [];
  for (const category of product.categories) {
    for (const article of category.articles) {
      all.push({ category, article });
    }
  }
  const i = all.findIndex(
    (e) => e.category.id === categoryId && e.article.id === articleId,
  );
  return {
    prev: i > 0 ? all[i - 1] : undefined,
    next: i < all.length - 1 ? all[i + 1] : undefined,
  };
}
